<template>
    <div id="visitor-history" class="container-fluid">

        <div id="visitor-history-filter-row">
            <datetime
                v-model="startDate"
                :max-datetime="maxDate"
                :min-datetime="lastFiveYear"
                :phrases="phrases"
                :placeholder="$t('message.startDate')"
                :locale="$i18n.locale"
                auto
                input-class="form-control rounded-0"
                input-id="start-date-input"
                type="datetime"
                @input="onStartDateChange"
            />
            <datetime
                v-model="endDate"
                :max-datetime="maxDate"
                :min-datetime="getMinEndDate"
                :phrases="phrases"
                :placeholder="$t('message.endDate')"
                :locale="$i18n.locale"
                auto
                input-class="form-control rounded-0"
                input-id="end-date-input"
                type="datetime"
                @input="onEndDateChange"
            />

            <CustomPicker
                :items="getChannelOptions()"
                label-key="label"
                id-key="id"
                :title="$t('message.selectChannel')"
                v-model="selectedChannel"
                @change="saveFilters"
                icon="hub"
            />
            <CustomPicker
                :items="getDepartmentOptions()"
                label-key="label"
                id-key="id"
                :title="$t('message.allDepartments')"
                v-model="selectedDepartment"
                @change="saveFilters"
                icon="business"
            />

            <CustomPicker
                :items="getStartOptions()"
                label-key="label"
                id-key="id"
                :title="$t('message.choosePoints')"
                v-model="point"
                @change="saveFilters"
                icon="star"
            />
            <CustomPicker
                :items="getAgentsOptions()"
                label-key="label"
                id-key="id"
                :title="$t('message.selectRepresentativeChatbot')"
                v-model="selectedUser"
                @change="saveFilters"
                icon="support_agent"
            />
            <CustomPicker
                :items="getDomainOptions()"
                label-key="label"
                id-key="id"
                :title="$t('message.selectDomain')"
                v-model="selectedCustomerDomains"
                @change="saveFilters"
                icon="travel_explore"
            />
            <a-input-search v-model="query" class="search-input" :placeholder="$t('message.search')" @change="saveFilters" />
        </div>
        <supsis-table
            :columns="columns"
            :rows="visitorData"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            :options="tableOptions"
            :bottomAlign="'space-between'"
            :headAlign="'right'"
            :downloadOptions="{
                downloadAll: true,
                downloadTable: true,
            }"
            :tableSettings="true"
            @cursor-changed="
                (cursor) => {
                    paginationCursor = cursor;
                    savePageSize(cursor.perPage);
                }
            "
            @download-table="exportToExcel"
            @download-all="downloadAsFile"
            @table-settings="openVisitorHistorySettingsModal"
        />

        <a-modal
            v-model="showDownloadModal"
            :title="$t('message.excelDownload')"
            :footer="null"
            @cancel="cancelDownloadModal"
            :width="400"
        >
            <div class="excel-download-modal">
                <div class="download-options">
                    <div class="option-title">{{ $t("message.excelExportReceiveQuestion") }}</div>

                    <div class="option-item" @click="selectOption('self')">
                        <div class="option-radio">
                            <a-radio :checked="selectedOption === 'self'" />
                        </div>
                        <div class="option-content">
                            <div class="option-label">{{ $t("message.sendToMe") }}</div>
                            <div class="option-description">{{ user && user.email ? user.email : '-' }}</div>
                        </div>
                    </div>

                    <div class="option-item" @click="selectOption('other')">
                        <div class="option-radio">
                            <a-radio :checked="selectedOption === 'other'" />
                        </div>
                        <div class="option-content">
                            <div class="option-label">{{ $t("message.sendToSomeoneElse") }}</div>
                        </div>
                    </div>

                    <!-- Başkasına Gönder seçilince gösterilecek email alanı -->
                    <div v-if="selectedOption === 'other'" class="email-input-area">
                        <a-input
                            v-model="customEmail"
                            :placeholder="$t('message.enterEmailAddress')"
                            class="custom-email-input"
                        >
                            <template #prefix>
                                <a-icon type="mail" style="color: rgba(0,0,0,.25)" />
                            </template>
                        </a-input>
                    </div>
                </div>

                <div class="modal-footer">
                    <a-button @click="cancelDownloadModal">{{ $t("message.cancelText") }}</a-button>
                    <a-button
                        type="primary"
                        class="submit-button"
                        @click="submitEmailRequest"
                        :disabled="!isValidEmailSelected"
                    >
                        {{ $t("message.send") }}
                    </a-button>
                </div>
            </div>
        </a-modal>

        <Modal2
            id="visitor-history-modal"
            v-if="visible"
            @cancel="visible = false"
            :visible="visible"
            :title="$t('message.visitorHistory')"
            :description="$t('message.visitorHistoryDescription')"
            :show-footer="false"
            :maskClosable="false"
            :width="1000"
        >
            <div v-if="filteredMessages.length" class="filteredMessages">
                <div class="filteredMessages">
                    <div class="parent-div">
                        <div>
                            <div class="display-flex">
                                <span
                                    class="icon material-icons"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('coupon.name')"
                                    >person</span
                                >
                                {{ selectedHistory.visitor.fullname || "-" }}
                            </div>
                        </div>
                        <div>
                            <div class="display-flex">
                                <span
                                    class="icon material-icons"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('coupon.email')"
                                    >mail</span
                                >
                                {{ selectedHistory.visitor.email || "-" }}
                            </div>
                        </div>
                        <div>
                            <div class="display-flex">
                                <span
                                    class="icon material-icons"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('coupon.phone')"
                                    >phone</span
                                >
                                {{ getPhoneNumber() }}
                            </div>
                        </div>
                        <div v-if="!visitor.isBlocked" @click="ban()">
                            <div class="display-flex">
                                <span
                                    class="icon material-icons"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('message.block_')"
                                    >block</span
                                >
                                {{ $t("message.block_") }}
                            </div>
                        </div>
                        <div v-else @click="unban()">
                            <div class="display-flex">
                                <span
                                    class="icon material-icons"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('message.block_')"
                                    >block</span
                                >
                                {{ $t("message.block_") }} Kaldır
                            </div>
                        </div>
                        <div class="display-flex">
                            <vue-excel-xlsx
                                :columns="downloadConversationFields()"
                                :data="downloadConversationData()"
                                :fileName="downloadConversationName"
                                :sheetName="'Page 1'"
                                class="download-button"
                                style="justify-self: end; width: 36px; align-self: center"
                            >
                                <md-icon class="material-icons">download</md-icon>
                            </vue-excel-xlsx>
                            {{ $t("message.download") }}
                        </div>
                    </div>
                </div>
                <div v-if="filteredMessages.length" v-chat-scroll class="messages">
                    <MessageList
                        :always-scroll-to-bottom="false"
                        :colors="colors"
                        :messages="getMessages()"
                        :participants="getParticipants()"
                        :contextMenuVisable="false"
                    />
                </div>
                <div class="back-and-forth-button">
                    <a-button @click="navigateHistory(-1)"> &lt; </a-button>
                    <a-button @click="navigateHistory(1)"> &gt; </a-button>
                </div>
            </div>
            <div v-else>
                <b-alert show variant="info">{{ $t("message.chatNotFound") }}</b-alert>
            </div>
        </Modal2>
        <Modal2
            v-if="visitorHistorySettingsModalVisible"
            id="tableColumns"
            :title="$t('message.tableSettings')"
            @cancel="closeVisitorHistorySettingsModal"
            :show-footer="false"
        >
            <div>{{ $t("message.selectColumns") }}</div>
            <div class="tableSettings">
                <template v-for="(col, i) in defaultCols">
                <div v-if="col.key !== 'actions'" :key="col.key">
                    <div class="item">
                        <div class="checkbox-group">
                            <b-form-checkbox
                                v-model="tableCols[col.key]"
                                :disabled="
                                    Object.values(tableCols).filter((x) => x === true).length > 12 &&
                                    !tableCols[col.key] &&
                                    customLoginFormEnabled
                                "
                                @input="toLocalStorage"
                            >
                                {{ col.title }}
                            </b-form-checkbox>
                        </div>
                    </div>
                    <!-- Eğer i çift sayıdaysa bir sonraki çifti başlat -->
                    <div v-if="i % 2 === 1" class="clear"></div>
                </div>
                </template>
            </div>
        </Modal2>
    </div>
</template>

<script>
import moment from "moment";
import { Settings, DateTime } from "luxon";
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import MessageList from "./popup-chat/MessageList";
import { log2 } from "../utils/log2";
import { toNewMessagesModel } from "../utils/chat";
import PassedTimeCounterTimeFormat from "./PassedTimeCounterTimeFormat";
import { expertToExcel, isMaskContactInfo, maskContactInfo } from "../utils/utils";
import Pagination from "./Pagination";
import SearchIcon from "./svg/SearchIcon";
import { i18n } from "../language";
import { getOrUpdateFilters } from "../utils/persistenceStorage";
import EyesIcon from "./svg/EyesIcon.vue";
import Modal2 from "./common/Modal2.vue";
import IconButton from "../views/Pages/Settings/components/IconButton.vue";
import TimeUtils from "../utils/TimeUtils";
import SupsisTable from "./SupsisTable.vue";
import CustomPicker from "./pickers/CustomPicker.vue";
import ChatWatcher from "../ChatWatcherV2";

Settings.defaultLocale = i18n.locale;
const log = log2("VisitorHistory");

const defaultStartDate = moment().startOf("day").toISOString();
const defaultEndDate = moment().endOf("day").toISOString();

const filters = getOrUpdateFilters("chat-visitor-history");
export default {
    name: "VisitorHistory",
    props: ["timeSelect"],
    components: {
        CustomPicker,
        SupsisTable,
        IconButton,
        Pagination,
        MessageList,
        PassedTimeCounterTimeFormat,
        SearchIcon,
        EyesIcon,
        Modal2,
    },
    data() {
        return {
            selectedChannel: filters.selectedChannel || [],
            tableCols: null,
            defaultCols: [],
            visitor: {},
            visible: false,
            selectedCustomerDomains: filters.selectedCustomerDomains || null,
            renderCount: 0,
            point: filters.point,
            currentSort: {
                columnKey: "createdDate",
                order: "ascend",
            },
            rowIndex: 0,
            pageSizes: ["10", "25", "50", "100"],
            usersFilter: [],
            selectedUser: filters.selectedUser || null,
            selectedDepartment: null,
            selectedHistory: {
                visitor: {},
                messages: [],
            },
            loading: true,
            startDate: moment().startOf("day").toISOString(),
            endDate: moment().endOf("day").toISOString(),
            query: filters.query,
            maxDate: moment().toISOString(),
            lastFiveYear: moment().subtract(5, "year").toISOString(),
            pageSize: 10,
            current: !isNaN(filters.current) ? filters.current : 1,
            phrases: {
                ok: this.$t("message.ok"),
                cancel: this.$t("message.cancel"),
                months: this.getLocalizedMonths(),
                weekdays: this.getLocalizedWeekdays(),
            },
            colors: {
                header: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                launcher: {
                    bg: "#4e8cff",
                },
                messageList: {
                    bg: "#ffffff",
                },
                sentMessage: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                receivedMessage: {
                    bg: "#eaeaea",
                    text: "#222222",
                },
                userInput: {
                    bg: "#f4f7f9",
                    text: "#565867",
                },
            },
            visitorHistorySettingsModalVisible: false,
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: parseInt(localStorage.getItem("visitorHistoryPageSize")) || 10,
            },
            tableOptions: {
                rowStyleOption: {
                    stripe: true,
                },
            },
            visitorData: [],
            showDownloadModal: false,
            selectedOption: 'self',
            customEmail: '',
        };
    },
    computed: {
        ...mapState({
            channel: (state) => state.channel.channels,
            visitors: (state) => state.visitor.visitors,
            users: (state) => state.user.users.data,
            customLoginInputs: (state) => state.customLoginForm.inputs,
            subjects: (state) => state.subject.subjects.data,

            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            customerDomains: (state) => state.visitor.customerDomains,
            selectedSubject: (state) => state.subject.selectedSubject,
            channels: (state) => state.channel.channels,
            customLoginFormEnabled: (state) => {
                const enabledFlag = state.setting.settings.customLoginFormEnabled;
                const isValid = !!state.customLoginForm.inputs.filter((x) => x.visible).length;
                return enabledFlag && isValid;
            },
            maskSensitiveInfo: (state) => state.setting.settings.maskSensitiveInfo,
            isRoot: (state) => _.get(state, "auth.user.role.isRoot", false),
        }),
        ...mapGetters({
            getUser: "user/getUser",
            isCustomLoginFormEnabled: "customLoginForm/isCustomLoginFormEnabled",
            isDarkMode: "user/isDarkMode",
            myActiveConversations: "chat/getMyActiveConversations",
            userSubjects: "subject/getUserSubjects",
        }),
        filteredMessages() {
            log(
                "filteredMessages",
                this.selectedHistory.messages,
                this.selectedHistory.messages.filter((m) => "undefined" === typeof m.type)
            );
            return this.selectedHistory.messages.filter((m) => "undefined" === typeof m.type);
        },
        downloadConversationName() {
            return `${this.selectedHistory.visitor.fullname}-${moment(this.selectedHistory.visitor.createdDate).format(
                "DD MMM, HH-mm-ss"
            )}`;
        },
        inputs() {
            return this.customLoginInputs.reduce((acc, cur) => {
                acc[cur._id] = cur;
                return acc;
            }, {});
        },
        columns() {
            let cols = [
                {
                    title: this.$t("message.whatsappNumber"),
                    field: "whatsappNumber",
                    key: "whatsappNumber",
                    renderBodyCell: ({ row }) => {
                        return <div>{row.whatsappNumber || "N/A"}</div>;
                    },
                },
                {
                    title: this.$t("whatsappChannel.channelName"),
                    field: "visitor.channel",
                    key: "visitor.channel",
                    renderBodyCell: ({ row }) => {
                        return this.channelMap[row.channel] || "N/A";
                    },
                },
                {
                    title: this.$t("message.department"),
                    field: "subject",
                    key: "subject",
                    scopedSlots: { customRender: "subject" },
                    renderBodyCell: ({ row }) => {
                        return this.getSubject(row.subject);
                    },
                },
                {
                    title: "IP Address",
                    field: "ipAddress",
                    key: "ipAddress",
                    renderBodyCell: ({ row }) => {
                        return _.get(row, "ipAddress") || "N/A";
                    },
                },
                {
                    title: this.$t("message.relatedAdvertisement"),
                    field: "metaReferrals",
                    key: "metaReferrals",
                    renderBodyCell: ({ row }) => {
                        return row.visitor.metaReferrals && row.visitor.metaReferrals.length > 0
                            ? row.visitor.metaReferrals[0].headline
                            : "N/A";
                    },
                },
                {
                    title: this.$t("akinon.country"),
                    field: "country",
                    key: "geoLocation.country",
                    renderBodyCell: ({ row }) => {
                        return row.geoLocation ? row.geoLocation.country : "N/A";
                    },
                },
                {
                    title: this.$t("message.start"),
                    field: "createdDate",
                    key: "createdDate",
                    renderBodyCell: ({ row }) => {
                        return moment(row.createdDate).format("D MMMM, HH:mm");
                    },
                },
                {
                    title: this.$t("message.finish"),
                    field: "destroyedDate",
                    key: "destroyedDate",
                    renderBodyCell: ({ row }) => {
                        return moment(row.destroyedDate).format("D MMMM, HH:mm");
                    },
                },
                {
                    title: this.$t("message.timeSpent"),
                    field: "timeSpent",
                    key: "timeSpent",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div style="line-height: 0px;">
                                <PassedTimeCounterTimeFormat date={row.connectedDate} endDate={row.destroyedDate} />
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.representative_"),
                    field: "user",
                    key: "user",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div
                                v-tippy={{
                                    followCursor: true,
                                    placement: "bottom-end",
                                    theme: this.isDarkMode ? "light-border" : "google",
                                }}
                                content={this.getUserNames(row)}
                            >
                                {this.getUserNames(row).length > 15
                                    ? this.getUserNames(row).slice(0, 11) + "..."
                                    : this.getUserNames(row)}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.star"),
                    field: "star",
                    key: "star",
                    renderBodyCell: ({ row }) => {
                        return row.point || "-";
                    },
                },
                {
                    title: this.$t("message.transactions"),
                    key: "_id",
                    field: "_id",
                    align: "right",
                    renderBodyCell: ({ row }) => {
                        const renderStartWABusiness = () => {
                            return (
                                this.isWABusiness(row) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center classic-button"
                                        size="small"
                                        on-click={() => this.startWhatsappChat(row)}
                                    >
                                        <span style="margin-right: 3px; display: block; font-size: 12px">
                                            Start Chat
                                        </span>
                                    </a-button>
                                )
                            );
                        };

                        const renderStartWhatsappV1 = () => {
                            return (
                                this.isWhatsappV1(row) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center classic-button"
                                        size="small"
                                        on-click={() => this.startWhatsappChat(row)}
                                    >
                                        <span style="margin-right: 3px; display: block; font-size: 12px">
                                            Start Chat
                                        </span>
                                    </a-button>
                                )
                            );
                        };

                        const renderStartWhatsappV2 = () => {
                            return (
                                this.isWhatsappV2(row) && (
                                    <a-button
                                        class="actionButton d-flex align-items-center classic-button"
                                        size="small"
                                        on-click={() => this.startWhatsappChat(row)}
                                    >
                                        <span style="margin-right: 3px; display: block; font-size: 12px">
                                            Start Chat
                                        </span>
                                    </a-button>
                                )
                            );
                        };

                        return (
                            <div style="display: flex; flex-direction: column; justify-content: flex-end">
                                <span style="display: flex; flex-direction: row">
                                    <a-button
                                        class="actionButton d-flex align-items-center classic-button"
                                        size="small"
                                        on-click={() => this.clickLine(row)}
                                    >
                                        <span style="margin-right: 3px; display: block; font-size: 12px">
                                            {this.$t("message.userProfile")}
                                        </span>
                                    </a-button>
                                    {renderStartWABusiness()}
                                    {renderStartWhatsappV1()}
                                    {renderStartWhatsappV2()}
                                    <button
                                        class="actionButton d-flex align-items-center background-transparent"
                                        size="small"
                                        on-click={() => this.view(row._id, row.index)}
                                    >
                                        <EyesIcon />
                                    </button>
                                </span>
                            </div>
                        );
                    },
                },
            ];
            if (this.isCustomLoginFormEnabled) {
                let customCols = this.customLoginInputs
                    .filter((x) => !["email", "fullname", "whatsappNumber"].includes(x.type))
                    .map((x) => {
                        return {
                            title: x.name,
                            field: x.name,
                            key: x._id,
                        };
                    });
                cols = [
                    {
                        title: this.$t("message.fullName"),
                        field: "fullname",
                        key: "fullname",
                    },
                    {
                        title: this.$t("message.email"),
                        field: "email",
                        key: "email",
                    },
                    ...customCols,
                    ...cols,
                ];
            } else {
                cols = [
                    {
                        title: this.$t("message.fullName"),
                        field: "fullname",
                        key: "fullname",
                    },
                    {
                        title: this.$t("message.email"),
                        field: "email",
                        key: "email",
                    },
                    ...cols,
                ];
            }
            this.defaultCols = cols;
            this.tableCols =
                this.tableCols ||
                cols.reduce((acc, cur) => {
                    acc[cur.key] = true;
                    return acc;
                }, {});
            // log.debug("getCols called", { cols });
            return cols.filter((x) => this.tableCols[x.key]);
        },
        channelMap() {
            return this.channel.reduce((map, channel) => {
                map[channel._id] = channel.name;
                return map;
            }, {});
        },
        channelTypeMap() {
            return this.channel.reduce((map, channel) => {
                map[channel._id] = channel.type;
                return map;
            }, {});
        },
        isValidEmailSelected() {
            if (this.selectedOption === 'self') {
                return this.user && this.user.email;
            } else if (this.selectedOption === 'other') {
                return this.customEmail && this.customEmail.includes('@');
            }
            return false;
        },
        getMinEndDate() {
            if (!this.startDate) return this.lastFiveYear;

            const start = moment(this.startDate);
            const maxRange = start.clone().add(45, 'days');

            if (maxRange.isAfter(moment())) {
                return this.startDate;
            }

            return this.startDate;
        }
    },
    methods: {
        ...mapActions({
            getVisitors: "visitor/getVisitors",
            loadOldMessages: "chat/loadOldMessages",
            getUsers: "user/getUsers",
            getCustomerDomainsIfNeed: "visitor/getCustomerDomainsIfNeed",
            banVisitor: "visitor/banVisitor",
            unbanVisitor: "visitor/unbanVisitor",
            addNewWindow: "app/addNewWindow",
            downloadChatHistoryExcel: "visitor/downloadChatHistoryExcel",
        }),
        isMaskContactInfo,
        isShouldMask(visitor, val) {
            return (
                !this.isRoot &&
                this.maskSensitiveInfo &&
                (val === visitor.email || val === visitor.whatsappNumber || val === visitor.phone)
            );
        },
        maskContactInfo(visitor, key, length = 4) {
            return this.isMaskContactInfo(key)
                ? maskContactInfo(visitor[key], length)
                : this.isShouldMask(visitor, visitor[key])
                ? maskContactInfo(visitor[key], length)
                : visitor[key];
        },
        getAgentsOptions() {
            let items = _.get(this, "usersFilter.data", []) || [];
            items = items.map((item) => {
                return {
                    id: item._id,
                    label: item.name,
                };
            });
            items = [
                {
                    id: null,
                    label: this.$t("message.allUsers"),
                },
                {
                    id: "only-chatbot",
                    label: this.$t("message.chatBot"),
                },
                ...items,
            ];
            return items;
        },
        getDepartmentOptions() {
            let items = this.userSubjects || [];
            items = items.map((item) => {
                const languages = Object.keys(item.languageContent);
                const defaultLanguage = languages.find((lang) => lang === i18n.locale) || languages[0];
                return {
                    id: item._id,
                    label: _.get(item.languageContent, [defaultLanguage, "name"]),
                };
            });
            items = [
                {
                    id: null,
                    label: this.$t("message.allDepartments"),
                },
                ...items,
            ];
            return items;
        },
        getDomainOptions() {
            let items = _.get(this, "customerDomains", []) || [];
            items = items.map((item) => {
                return {
                    id: item,
                    label: item,
                };
            });
            items = [
                {
                    id: null,
                    label: this.$t("message.allSites"),
                },
                ...items,
            ];
            return items;
        },
        getStartOptions() {
            let items = [
                {
                    id: 1,
                    label: "⭐",
                },
                {
                    id: 2,
                    label: "⭐⭐",
                },
                {
                    id: 3,
                    label: "⭐⭐⭐",
                },
                {
                    id: 4,
                    label: "⭐⭐⭐⭐",
                },
                {
                    id: 5,
                    label: "⭐⭐⭐⭐⭐",
                },
            ];
            items = [
                {
                    id: null,
                    label: this.$t("chatbotv2.all"),
                },
                ...items,
            ];
            return items;
        },
        getChannelOptions() {
            let items = this.channels.map((item) => {
                return {
                    id: item._id,
                    label: item.name,
                };
            });
            items = [
                {
                    id: null,
                    label: this.$t("message.allChannels"),
                },
                ...items,
            ];
            return items;
        },
        toLocalStorage() {
            localStorage.setItem("visitorHistoryTableCols", JSON.stringify(this.tableCols));
        },
        saveDate() {
            this.$emit("customTime");
            this.saveFilters();
        },
        saveFilters() {
            getOrUpdateFilters("chat-visitor-history", {
                startDate: this.startDate,
                endDate: this.endDate,
                query: this.query,
                point: this.point,
                selectedUser: this.selectedUser,
                selectedCustomerDomains: this.selectedCustomerDomains,
                subjectId: this.selectedDepartment,
            });
        },
        handleResetFilters(resetValues) {
            this.$emit("timeSelectChanged", "today");
            this.startDate = resetValues.startDate;
            this.endDate = resetValues.endDate;
            this.query = resetValues.query;
            this.point = resetValues.point;
            this.selectedUser = resetValues.selectedUser;
            this.selectedCustomerDomains = resetValues.selectedCustomerDomains;
            this.getHistory();
        },
        isWABusiness(chat) {
            const channelId = _.get(chat, "channel");
            const channel = this.channels.find((c) => c._id === channelId);
            return _.get(channel, "type") === "whatsapp-business-api";
        },
        isWhatsappV1(chat) {
            const channelId = _.get(chat, "channel");
            const channel = this.channels.find((c) => c._id === channelId);
            return _.get(channel, "type") === "whatsapp-v1" || _.get(channel, "type") === "whatsapp-qr";
        },
        isWhatsappV2(chat) {
            const channelId = _.get(chat, "channel");
            const channel = this.channels.find((c) => c._id === channelId);
            return _.get(channel, "type") === "whatsapp-v2";
        },
        startWhatsappChat(chat) {
            const channelId = _.get(chat, "channel");
            const whatsappNumber = _.get(chat, "whatsappNumber");
            if (!whatsappNumber) return;
            if (!channelId) return;

            ChatWatcher.startChatImmediately({
                whatsappNumber: whatsappNumber,
                channelId: channelId,
                channelType: this.channelTypeMap[channelId],
            });
        },
        getPhoneNumber() {
            const findCustomLoginInfo = this.customLoginInputs.find((v) => v.type === "phone");
            const phoneNumber = (this.selectedHistory.visitor.customLoginInfos || []).find(
                (v) => v.input === findCustomLoginInfo?._id
            );
            return phoneNumber?.value || "-";
        },
        navigateHistory(direction) {
            const currentId = this.selectedHistory.visitor._id;
            const currentIndex = this.visitors.data.findIndex((v) => v._id === currentId);
            const newIndex = currentIndex + direction;

            if (newIndex >= 0 && newIndex < this.visitors.data.length) {
                const newId = this.visitors.data[newIndex]._id;
                this.view(newId, newIndex);
            }
        },
        getCustomLoginInfos() {
            let inputValues = _.get(this.selectedHistory, "visitor.customLoginInfos", []);
            log.info("getCustomLoginInfos", { inputValues });
            inputValues = inputValues
                .filter((v) => this.inputs[v.input] && !!v.value)
                .map((v) => ({
                    value: this.inputs[v.input].type === "subject" ? this.getSubjectName(v.value) : v.value,
                    name: this.inputs[v.input].localizedLabel[this.$i18n.locale].text,
                }));
            log.info("getCustomLoginInfos", { inputValues });
            return inputValues;
        },
        getSubjectName(id) {
            let maybeSubject = this.subjects.find((x) => x._id == id);
            return maybeSubject ? maybeSubject.languageContent[i18n.locale].name : undefined;
        },
        getInputValue(data, input) {
            log.info("getInputValue", { data, input });
            let maybeInput = (data.customLoginInfos || []).find((x) => x.input === input._id);
            log.info("getInputValue", { data, input, maybeInput, customLoginInputs: this.customLoginInputs });
            if (maybeInput) {
                let inputSchema = this.customLoginInputs.find((x) => x._id === input._id);
                if (inputSchema.type === "subject") {
                    return this.getSubjectName(maybeInput.value);
                }
                return maybeInput.value;
            }
            if (input.type === "email") return data.email || "-";
            if (input.type === "fullname") return data.fullname || "-";
            return "-";
        },
        getSubject(item) {
            return this.getSubjectName(item?._id);
        },
        tableChange(pagination, filters, sorter) {
            /*log("tableChange", {pagination, filters, sorter});*/
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log.debug("sort debug", {
                oldSort: { ...oldSort },
                newSort: { ...newSort },
                eq: _.isEqual(newSort, oldSort),
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                log.debug("sort changed", {
                    newSort,
                });
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },
        setCustomDateFilter(filter) {
            log.debug("setCustomDateFilter ", filter);
            if (filter === "today") {
                this.startDate = moment().startOf("day").toISOString();
                this.endDate = moment().endOf("day").toISOString();
            } else if (filter === "yesterday") {
                this.startDate = moment().subtract(1, "days").startOf("day").toISOString();
                this.endDate = moment().subtract(1, "days").endOf("day").toISOString();
            } else if (filter === "current-week") {
                this.startDate = moment().startOf("week").startOf("day").toISOString();
                this.endDate = moment().endOf("day").toISOString();
            } else if (filter === "last-week") {
                this.startDate = moment().startOf("isoWeeks").subtract(1, "days").startOf("isoWeeks").toISOString();
                this.endDate = moment().startOf("isoWeeks").subtract(1, "days").endOf("isoWeeks").toISOString();
            } else if (filter === "current-month") {
                this.startDate = moment().startOf("month").startOf("day").toISOString();
                this.endDate = moment().endOf("day").toISOString();
            } else if (filter === "last-month") {
                this.startDate = moment().startOf("month").subtract(1, "days").startOf("month").toISOString();
                this.endDate = moment().startOf("month").subtract(1, "days").endOf("month").toISOString();
            } else if (filter === "last-30") {
                this.startDate = moment().subtract(30, "days").startOf("day").toISOString();
                this.endDate = moment().endOf("day").toISOString();
            } else if (filter === "all-time") {
                this.startDate = null;
                this.endDate = null;
            } else if (typeof filter === "object") {
                this.startDate = filter.start;
                this.endDate = filter.end;
            }
        },
        getLast11Months() {
            return TimeUtils.getLast11Months();
        },
        getUserNames(item) {
            if (!item) return "-";
            
            const users = item.users && item.users.length > 0 
                ? _.uniqBy(item.users, (q) => q && q._id) 
                : [item.connectedUser];
                
            return users
                .filter(x => x !== null && x !== undefined)
                .map((x) => this.getUserName(x))
                .join(" ; ") || "-";
        },
        getUserName(userObjectOrUserId) {
            if (!userObjectOrUserId) return "-";
            
            const isUserObject = typeof userObjectOrUserId === "object" && userObjectOrUserId !== null;
            const isUserId = typeof userObjectOrUserId === "string";
            
            if (isUserObject) {
                return userObjectOrUserId.name || "-";
            } else if (isUserId) {
                let newUser = "-";
                const maybeUserObj = this.users !== null && this.users && this.users.find((x) => x._id === userObjectOrUserId);
                if (maybeUserObj) {
                    newUser = maybeUserObj.name || "-";
                }
                log("getUserHelper", userObjectOrUserId, newUser, maybeUserObj);
                return newUser;
            } else {
                return "-";
            }
        },
        getParticipants() {
            const supportUsers = this.filteredMessages
                .filter((x) => x.messageType === "user" || x.messageType === "private")
                .map((x) => ({
                    id: x.userId,
                    name: x.username,
                }));

            return _.uniqBy(supportUsers, (q) => q.name);
        },
        getMessages() {
            let allMessages = toNewMessagesModel(this.filteredMessages, this);

            if (this.typingMessage) {
                allMessages = [
                    ...allMessages,
                    {
                        type: "typing",
                        data: {
                            text: this.typingMessage,
                        },
                    },
                ];
            }

            return allMessages;
        },
        getHistory(resetPage = false) {
            if (resetPage) {
                this.paginationCursor.page = 1;
            }

            this.loading = true;
            log("getHistory running ", { query: this.query });
            const skipCount =
                this.paginationCursor.page * this.paginationCursor.perPage - this.paginationCursor.perPage;

            getOrUpdateFilters("chat-visitor-history", {
                pageSize: this.paginationCursor.perPage,
                current: this.paginationCursor.page,
                startDate: this.startDate,
                endDate: this.endDate,
                query: this.query,
                point: this.point,
                selectedUser: this.selectedUser,
                selectedCustomerDomains: this.selectedCustomerDomains,
                selectedChannel: this.selectedChannel,
                subjectId: this.selectedDepartment,
            });

            this.getVisitors({
                limit: this.paginationCursor.perPage,
                skipCount: skipCount > 0 ? skipCount : 0,
                startDate: this.startDate,
                endDate: this.endDate,
                query: this.query,
                point: this.point,
                userId: this.selectedUser,
                homeSite: this.selectedCustomerDomains,
                channelIds: this.selectedChannel,
                subjectId: this.selectedDepartment,
            }).finally(() => (this.loading = false));
        },
        view(id, i) {
            const selectedVisitor = this.visitors.data.find((v) => v._id === id);
            const rowIndex = i;
            this.loadOldMessages(id).then((response) => {
                let items = response.data.data;
                items = _.orderBy(
                    _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                    (q) => moment(q.createdDate).toDate().getTime(),
                    "asc"
                );
                this.selectedHistory = {
                    visitor: selectedVisitor,
                    messages: items,
                    rowIndex: rowIndex,
                };
                this.visible = true;
            });
        },
        downloadConversationData() {
            let rawData = this.selectedHistory.messages.filter(
                (x) => x.messageType === "visitor" || x.messageType === "user"
            );
            let formattedData = rawData.map((x) => {
                let name;
                let date = moment(x.createdDate).format("HH:mm:ss");
                let message = (x.description || "").replace(/(<([^>]+)>)/gi, "");
                if (x.messageType === "visitor") {
                    name = this.selectedHistory.visitor.fullname;
                } else {
                    name = `${x.username}(${this.$t("message.representative_")})`;
                }
                return {
                    name: name,
                    date: date,
                    message: message,
                };
            });
            return formattedData;
        },
        downloadConversationFields() {
            return [
                {
                    label: this.$t("message.username"),
                    field: "name",
                },
                {
                    label: this.$t("message.date"),
                    field: "date",
                },
                {
                    label: this.$t("message.message"),
                    field: "message",
                },
            ];
        },
        reset() {
            this.selectedHistory = {
                visitor: {},
                messages: [],
            };
        },
        fetchAllUsers() {
            this.getUsers({ perPage: 1000, page: 1 }).then((response) => {
                // log("getUsers completed", response.data);
                this.usersFilter = response.data;
            });
        },
        queryChanged: _.debounce(function (newQuery, oldQuery) {
            // log("queryChanged", { newQuery, oldQuery });
            if (newQuery !== oldQuery) {
                this.page = 1;
                this.getHistory(true);
            }
        }, 1000),
        getExcellData() {
            return this.visitors.data.map((x) => {
                const customLoginInfo = {};
                (x.customLoginInfos || []).forEach((info) => {
                    const inputMeta = this.customLoginInputs.find((input) => input._id === info.input);
                    if (inputMeta) {
                        customLoginInfo[inputMeta.name] = info.value;
                    }
                });

                return {
                    ...x,
                    country: x.geoLocation ? x.geoLocation.country : "N/A",
                    user: this.getAgentNames(x) || "-",
                    createdDate: moment(x.createdDate).format("DD MMM, HH:mm:ss"),
                    destroyedDate: x.destroyedDate ? moment(x.destroyedDate).format("DD MMM, HH:mm:ss") : "-",
                    timeSpent: x.destroyedDate
                        ? Math.ceil(moment.duration(moment(x.destroyedDate).diff(x.createdDate)).asMinutes()) + "dk"
                        : "-",
                    subject: this.getSubjectName(x.subject),
                    comment: x.description,
                    ...customLoginInfo,
                };
            });
        },
        getFileName() {
            return "conversation-history.xlsx";
        },
        getAgentNames(chat) {
            if (!chat || !chat.users || !Array.isArray(chat.users)) return "-";
            if (!this.users || !Array.isArray(this.users)) return "-";
            
            return chat.users
                .filter(id => id !== null && id !== undefined)
                .map((id) => {
                    const u = this.users.find((x) => x && x._id === id);
                    return u && u.name ? u.name : "";
                })
                .filter(name => name !== "")
                .join(",") || "-";
        },
        clickLine(item, index) {
            if (!item || !item.visitor) return;
            
            this.addNewWindow({
                type: "show-visitor",
                title: this.$t("task.whatsappContactProfileTitle", {
                    name: item.visitor.fullname || "-",
                    number: item.visitor.phoneNumber || "-",
                }),
                payload: {
                    visitorId: item.visitor._id,
                },
                footer: false,
            });
        },
        ban() {
            const visitorId = this.visitors.data[this.rowIndex].visitor._id;
            this.banVisitor(visitorId);
            this.selectedHistory.visitor.isBlocked = true;
            this.$message.success(this.$t("coupon.visitorBan"));
        },
        unban() {
            const visitorId = this.visitors.data[this.rowIndex].visitor._id;
            this.unbanVisitor(visitorId);
            this.selectedHistory.visitor.isBlocked = false;
            this.$message.success(this.$t("coupon.visitorUnBlock"));
        },
        exportToExcel() {
            const dataToExport = this.getExcellData();
            expertToExcel(dataToExport, this.getFileName(), "Sheet 1");
        },
        downloadAsFile() {
            const lastDownloadTime = localStorage.getItem('lastExcelDownloadTime');
            const currentTime = Date.now();

            if (lastDownloadTime && currentTime - parseInt(lastDownloadTime) < 600000) {
                const remainingMillis = 600000 - (currentTime - parseInt(lastDownloadTime));
                const remainingMinutes = Math.floor(remainingMillis / 60000);
                const remainingSeconds = Math.floor((remainingMillis % 60000) / 1000);

                let timeParts = [];
                if (remainingMinutes > 0) {
                    timeParts.push(this.$t("message.minutesText", { count: remainingMinutes }));
                }
                if (remainingSeconds > 0 || remainingMinutes === 0) {
                    timeParts.push(this.$t("message.secondsText", { count: remainingSeconds }));
                }

                const timeString = timeParts.join(" ");
                this.$message.warning(this.$t("message.downloadCooldownWarning", { time: timeString }));
                return;
            }

            this.selectedOption = 'self';
            this.customEmail = '';
            this.showDownloadModal = true;
        },
        selectOption(option) {
            this.selectedOption = option;
        },
        cancelDownloadModal() {
            this.showDownloadModal = false;
            this.selectedOption = 'self';
            this.customEmail = '';
        },
        submitEmailRequest() {
            const email = this.selectedOption === 'self' ? (this.user && this.user.email ? this.user.email : '') : this.customEmail;
            if (!email) {
                this.$message.error(this.$t("message.emailRequired"));
                return;
            }
            this.sendExcelToEmail(email);

            localStorage.setItem('lastExcelDownloadTime', Date.now().toString());
        },
        sendExcelToEmail(email) {
            const params = {
                startDate: this.startDate,
                endDate: this.endDate,
                query: this.query,
                point: this.point,
                userId: this.selectedUser,
                homeSite: this.selectedCustomerDomains,
                channelIds: this.selectedChannel,
                subjectId: this.selectedDepartment,
                email: email
            };

            this.showDownloadModal = false;
            this.$message.success(this.$t("message.exportRequestSuccess"));

            this.downloadChatHistoryExcel(params)
                .then((data) => {
                    console.log("Excel talebi tamamlandı:", data);
                })
                .catch((err) => {
                    console.error("Excel oluşturma hatası:", err);
                });
        },
        openVisitorHistorySettingsModal() {
            this.visitorHistorySettingsModalVisible = true;
        },
        closeVisitorHistorySettingsModal() {
            this.visitorHistorySettingsModalVisible = false;
        },
        filterByChannel() {
            this.getHistory(true);
        },
        savePageSize(size) {
            localStorage.setItem("visitorHistoryPageSize", size);
            this.getHistory(false);
        },
        getLocalizedMonths() {
            const locale = this.$i18n.locale;
            return [
                this.$t("datetime.january"),
                this.$t("datetime.february"),
                this.$t("datetime.march"),
                this.$t("datetime.april"),
                this.$t("datetime.may"),
                this.$t("datetime.june"),
                this.$t("datetime.july"),
                this.$t("datetime.august"),
                this.$t("datetime.september"),
                this.$t("datetime.october"),
                this.$t("datetime.november"),
                this.$t("datetime.december")
            ];
        },
        getLocalizedWeekdays() {
            const locale = this.$i18n.locale;
            return [
                this.$t("datetime.sunday"),
                this.$t("datetime.monday"),
                this.$t("datetime.tuesday"),
                this.$t("datetime.wednesday"),
                this.$t("datetime.thursday"),
                this.$t("datetime.friday"),
                this.$t("datetime.saturday")
            ];
        },
        updateDatetimeLocalization() {
            Settings.defaultLocale = this.$i18n.locale;
            this.phrases = {
                ok: this.$t("message.ok"),
                cancel: this.$t("message.cancel"),
                months: this.getLocalizedMonths(),
                weekdays: this.getLocalizedWeekdays(),
            };
        },
        onStartDateChange() {
            const start = moment(this.startDate);
            const end = moment(this.endDate);
            const maxEndDate = start.clone().add(45, 'days');

            if (end.diff(start, 'days') > 45) {
                this.endDate = maxEndDate.toISOString();
                this.$message.info(this.$t("message.maxDateRangeWarning"));
            }

            this.saveDate();
        },
        onEndDateChange() {
            const start = moment(this.startDate);
            const end = moment(this.endDate);

            if (end.diff(start, 'days') > 45) {
                const newStartDate = end.clone().subtract(45, 'days').toISOString();
                this.startDate = newStartDate;
                this.$message.info(this.$t("message.maxDateRangeWarning"));
            }

            this.saveFilters();
        }
    },
    mounted() {
        this.setCustomDateFilter(this.timeSelect);
        moment.locale(this.$i18n.locale);
        this.fetchAllUsers();
        this.getHistory();
        window.addEventListener("keydown", (e) => {
            if (e.key === "ArrowDown" && (e.ctrlKey || e.metaKey)) {
                if (this.rowIndex < 10) {
                    this.view(this.visitors.data[this.rowIndex + 1]._id, this.rowIndex + 1);
                }
                e.preventDefault();
            } else if (e.key === "ArrowUp" && (e.ctrlKey || e.metaKey)) {
                if (this.rowIndex > 0) {
                    this.view(this.visitors.data[this.rowIndex - 1]._id, this.rowIndex - 1);
                }
                e.preventDefault();
            }
        });
        this.getCustomerDomainsIfNeed();
        this.updateDatetimeLocalization();
    },
    created() {
        const savedPageSize = localStorage.getItem("visitorHistoryPageSize");
        if (savedPageSize) {
            this.paginationCursor.perPage = parseInt(savedPageSize, 10);
        }
        this.updateDatetimeLocalization();
    },
    updated() {
        if (moment.locale() !== this.$i18n.locale) moment.locale(this.$i18n.locale);
        // log("visitor history data ", this.visitors.data);
    },
    watch: {
        visitors(newValue, oldValue) {
            this.paginationCursor.total = newValue.total;
        },
        "visitors.data": {
            handler(newVal) {
                this.visitorData = newVal.map((v) => {
                    v.fullname = this.maskContactInfo(v, "fullname", 5);
                    v.email = this.maskContactInfo(v, "email", 5);
                    v.phone = this.maskContactInfo(v, "phone", 5);
                    v.whatsappNumber = this.maskContactInfo(v, "whatsappNumber", 5);
                    return v;
                });
            },
            deep: true,
        },
        startDate(newValue, oldValue) {
            if (newValue) {
                this.page = 1;
                this.getHistory(true);
            }
        },
        endDate(newValue, oldValue) {
            if (newValue) {
                this.page = 1;
                this.getHistory(true);
            }
        },
        paginationCursor: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal.page !== oldVal.page || newVal.perPage !== oldVal.perPage) {
                    this.getHistory(false);
                }
            },
        },
        selectedUser(newValue, oldValue) {
            this.page = 1;
            this.getHistory(true);
        },
        selectedDepartment(newValue, oldValue) {
            this.page = 1;
            this.getHistory(true);
        },
        query(newValue, oldValue) {
            this.queryChanged(newValue, oldValue);
            this.query = newValue;
        },
        point(newValue, oldValue) {
            this.point = newValue;
            this.getHistory(true);
        },

        timeSelect(newVal, oldVal) {
            this.setCustomDateFilter(newVal);
        },
        selectedCustomerDomains(newVal) {
            this.getHistory();
        },
        selectedSubject() {
            this.getHistory(true);
        },
        selectedChannel(newVal) {
            this.filterByChannel();
        },
        '$i18n.locale': {
            handler(newLocale) {
                moment.locale(newLocale);
                this.updateDatetimeLocalization();
                this.$forceUpdate();
            },
            immediate: true
        }
    },
};
</script>

<style lang="scss">
#channel-filter {
    .ant-select-selection {
        background-color: #ffffff !important;
        border: transparent !important;
        height: 5px !important;
        overflow: auto !important;
    }
}

#visitor-history {
    padding: 30px;
}

.new-theme {
    .custom-input input,
    .custom-input ul,
    .custom-input select {
        background-color: var(--white);
        border: 1px solid var(--color-border-default);
        border-radius: 6px !important;
        height: 30px;
    }

    .actionButton {
        border: none;
        box-shadow: none;
    }

    .custom-input ul {
        padding: 4px 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
    }

    #visitor-history {
        .excel-style {
            border: none;
            position: absolute;
            left: 10px;
            top: 30px;
            background: var(--white);
            box-shadow: 0 0 4px #00000052;
            border-radius: 5px;
            padding: 4px 11px;
            display: none;
        }

        &:hover .excel-style {
            display: block;
        }
    }

    #visitor-history-filter-row {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        align-items: center;
        padding-bottom: 5px;

        .ant-input-search {
            width: 200px;
        }
    }
}

.background-transparent {
    background-color: transparent !important;
}

#visitor-history-modal .messages {
    max-height: 500px !important;

    .sc-message-list {
        width: 100% !important;
    }
}

#visitor-history-modal .custom-modal-body {
    width: 1000px !important;
}

#visitor-history-modal .ant-modal-content {
    width: 1000px !important;
    position: relative;
    right: 50%;
}

#visitor-history-modal .icon {
    margin-right: 8px;
    color: #ffa800;
}
</style>
<style lang="scss">
.userInfo {
    font-family: Poppins;

    &.first {
        color: #3699ff;
        font-size: 16px;
        font-weight: 500;
        padding-top: 15px;
        line-height: 1;
    }

    &.sec {
        color: #181c32;
        font-size: 14px;
        font-weight: 300;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        max-width: 300px;
        -webkit-box-orient: vertical;
    }
}
</style>

<style lang="scss" scoped>
.tableSettingsBtn {
    position: absolute !important;
    left: 8px !important;
    top: 55px !important;
}

.item {
    margin-bottom: 10px;
}

.checkbox-group b-form-checkbox {
    display: block;
    margin-bottom: 5px;
}

.clear {
    clear: both;
}

.ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    max-width: 300px;
    -webkit-box-orient: vertical;
}

.classic-button {
    border: none;
    background-color: #ffa80040;
    color: #ffa800;
    font-family: Poppins;
    font-weight: 600 !important;
    font-size: 14px;
    cursor: pointer;
    padding: 3px 10px;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 5px;
}

.parent-div {
    display: flex;
    justify-content: space-between;
    gap: 40px;
}

.download-button {
    background: transparent;
    border-color: transparent;

    .md-icon.md-theme-default.md-icon-font {
        color: #ffa800;
    }
}

.back-and-forth-button {
    display: flex;
    justify-content: end;
    margin-top: 20px;

    .ant-btn {
        font-size: 20px;
        color: #ffa800;
        margin: 2px;
    }

    .ant-btn:hover {
        border-color: #ffa800;
    }

    .ant-btn:focus {
        border-color: #ffa800;
    }

    span {
        color: #ffa800;
        margin: 0px 10px;
        font-size: 30px;
    }
}

.filteredMessages {
    display: flex;
    flex-direction: column;
}

.display-flex {
    display: flex;
}

.excel-download-modal {
    .download-options {
        margin-bottom: 20px;

        .option-title {
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 16px;
            color: #262626;
        }

        .option-item {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border-radius: 6px;
            margin-bottom: 8px;
            cursor: pointer;
            transition: background-color 0.2s;

            &:hover {
                background-color: #f5f5f5;
            }

            .option-radio {
                margin-right: 12px;
            }

            .option-content {
                flex: 1;

                .option-label {
                    font-weight: 500;
                    margin-bottom: 2px;
                }

                .option-description {
                    font-size: 12px;
                    color: #8c8c8c;
                }
            }
        }

        .email-input-area {
            padding: 16px;
            margin-top: 4px;
            background-color: #f9f9f9;
            border-radius: 6px;

            .custom-email-input {
                width: 100%;
                border-radius: 4px;
            }
        }
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 24px;

        .submit-button {
            margin-left: 12px;
        }
    }
}
</style>
